// pages/PostProductionPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import axiosInstance from '../utils/axiosConfig';
import {
  getPostProductionByPerformance,
  createPostProduction,
  updatePostProduction,
  updatePostProductionStatus,
} from '../services/postProductionService';
import { PostProductionData } from '../types/PostProduction';

// Import all 7 component sections
import SalesDataSection from '../components/PostProduction/Salesdatasection';
import TechnicalReportSection from '../components/PostProduction/TechnicalReportSection';
import AudienceFeedbackSection from '../components/PostProduction/Audiencefeedbacksection';
import ArtistFeedbackSection from '../components/PostProduction/Artistfeedbacksection';
import MediaContentSection from '../components/PostProduction/Mediacontentsection';
import PaymentStatusSection from '../components/PostProduction/PaymentStatusSection';
import SummarySection from '../components/PostProduction/SummarySection';

// Type alias for easier reference
type APIPostProductionData = PostProductionData;
type PostProductionSection = keyof APIPostProductionData;

// Initial empty data structure
const initialPostProductionData: APIPostProductionData = {
  performanceId: '',
  salesData: {
    titoSales: 0,
    walkupSales: 0,
    walkupRevenue: 0,
    noShows: 0,
    guests: 0,
    totalAttendance: 0,
    titoRevenue: 0,
    grossRevenue: 0,
    refunds: 0,
    netRevenue: 0,
    ticketTypes: [],
  },
  technicalReport: {
    soundQuality: 0,
    lightingQuality: 0,
    backlineStatus: 'excellent',
    maintenanceRequired: false,
    maintenanceNotes: '',
    setupIssues: '',
    technicianNotes: '',
  },
  audienceFeedback: {
    overallRating: 0,
    audienceDemographics: '',
    audienceEngagement: 'good',
    noteworthy: '',
    complaints: '',
    suggestions: '',
  },
  artistFeedback: {
    artistSatisfaction: 0,
    venueRating: 0,
    hospitalityRating: 0,
    returnInterest: false,
    artistComments: '',
    staffNotes: '',
  },
  mediaContent: {
    photosUploaded: false,
    videoRecorded: false,
    youtubeUploaded: false,
    youtubeUrl: '',
    socialMediaPosts: [],
  },
  paymentStatus: {
    invoiceReceived: false,
    amountPaid: 0,
    paymentMethod: 'bank_transfer',
    unforeseen_costs: [],
    bookkeeping_notified: false,
  },
  summary: {
    success_rating: 0,
    highlights: '',
    challenges: '',
    lessons_learned: '',
    follow_up_actions: '',
  },
  status: 'draft',
  lastUpdated: new Date().toISOString(),
};

const PostProductionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [postProductionData, setPostProductionData] = useState<APIPostProductionData>(initialPostProductionData);
  const [performance, setPerformance] = useState<any>(null);

  // Helper function to safely convert to number
  const toNum = (v: any) => (typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) || 0 : 0);

  // Function to generate Tito slug from performance
const generateTitoSlug = (performance: any): string => {
  const artistName = performance.simpleBandName || 
    (typeof performance.band === 'object' ? performance.band?.artistName : '');
  const venueName = typeof performance.venue === 'object' ? 
    performance.venue?.name?.toLowerCase() : 'cooler';  // ADD .toLowerCase() HERE
  const date = new Date(performance.startTime);
  const month = date.toLocaleString('en-US', { month: 'short' }).toLowerCase();
  const year = date.getFullYear().toString().slice(-2);
  
  const cleanText = (text: string) => {
    return text
      .toLowerCase()
      .replace(/^the\s+/i, '')
      .replace(/\s+the\s+/g, ' ')
      .replace(/['']/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };
  
  const cleanArtistName = cleanText(artistName || '');
  const cleanVenueName = cleanText(venueName || '');
  const slug = `${cleanArtistName}-${cleanVenueName}-${month}${year}`;
  
  return slug;
};

// Fetch Tito sales data
const fetchTitoSalesData = async (slug: string) => {
  try {
    // Fetch both releases (ticket types) and registrations (sales)
    const [releasesResponse, registrationsResponse] = await Promise.all([
      axiosInstance.get(`/api/tito/${slug}/releases`),
      axiosInstance.get(`/api/tito/${slug}/registrations`)
    ]);
    
    if (releasesResponse.data && registrationsResponse.data) {
      const releases = releasesResponse.data.releases || [];
      const registrations = registrationsResponse.data.registrations || [];
      
      // Calculate ticket types with sold counts
      const ticketTypes = releases.map((release: any) => ({
        name: release.title,
        quantity: release.tickets_count || 0,
        price: parseFloat(release.price) || 0
      }));
      
      const totalSold = ticketTypes.reduce((sum: number, type: any) => sum + type.quantity, 0);
      const totalRevenue = ticketTypes.reduce((sum: number, type: any) => sum + (type.quantity * type.price), 0);
      
      return {
        totalSold,
        totalRevenue,
        ticketTypes,
      };
    }
    return null;
  } catch (err) {
    console.error('Error fetching Tito data:', err);
    return null;
  }
};

  // Merge Tito data into sales data
  const mergeTitoIntoSales = (tito: { totalSold: number; totalRevenue: number; ticketTypes: any[] }) => {
    setPostProductionData(prev => {
      const titoSales = toNum(tito.totalSold);
      const titoRevenue = toNum(tito.totalRevenue);
      const walkupRevenue = toNum(prev.salesData.walkupRevenue);
      const refunds = toNum(prev.salesData.refunds);
      const walkupSales = toNum(prev.salesData.walkupSales);
      const guests = toNum(prev.salesData.guests);

      return {
        ...prev,
        salesData: {
          ...prev.salesData,
          titoSales,
          titoRevenue,
          grossRevenue: titoRevenue + walkupRevenue,
          netRevenue: titoRevenue + walkupRevenue - refunds,
          totalAttendance: titoSales + walkupSales + guests,
          ticketTypes: (tito.ticketTypes || []).map(t => ({
            name: t.name,
            quantity: toNum(t.quantity),
            price: toNum(t.price)
          }))
        }
      };
    });
  };

  // Refresh Tito data handler
const handleRefreshFromTito = async () => {
  if (!performance) {
    console.log('No performance data available');
    return;
  }
  
  console.log('POSTPRODUCTION - Full performance object:', performance);
  console.log('POSTPRODUCTION - simpleBandName:', performance.simpleBandName);
  console.log('POSTPRODUCTION - band object:', performance.band);
  console.log('POSTPRODUCTION - venue object:', performance.venue);
  
  const artistName = performance.simpleBandName || 
    (typeof performance.band === 'object' ? performance.band?.artistName : '');
  const venueName = typeof performance.venue === 'object' ? performance.venue?.name : '';
  const date = performance.startTime ? new Date(performance.startTime) : new Date();
  
  console.log('POSTPRODUCTION - Artist name extracted:', artistName);
  console.log('POSTPRODUCTION - Venue name extracted:', venueName);
  console.log('POSTPRODUCTION - Date:', date);
  
  try {
    setLoading(true);
    const slug = generateTitoSlug(performance);
    console.log('POSTPRODUCTION - Generated slug:', slug);
    
    const live = await fetchTitoSalesData(slug);
    console.log('Fetched Tito data:', live);
    
    if (live) {
      mergeTitoIntoSales(live);
      setSuccessMessage('Tito data refreshed successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } else {
      setError('Failed to fetch Tito data');
    }
  } catch (err) {
    console.error('Error refreshing Tito data:', err);
    setError('Failed to refresh Tito data');
  } finally {
    setLoading(false);
  }
};

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (!id) {
          throw new Error("No performance ID provided");
        }
        
        // Fetch performance data
        const performanceRes = await axiosInstance.get(`/api/performances/${id}`);
        setPerformance(performanceRes.data);
        
        // Check if post-production report exists
        const postProductionRes = await getPostProductionByPerformance(id);
        
        if (postProductionRes.success && postProductionRes.data) {
          // Use existing report
          setPostProductionData(postProductionRes.data);
          
          // Refresh Tito data
          const slug = generateTitoSlug(performanceRes.data);
          const live = await fetchTitoSalesData(slug);
          if (live) mergeTitoIntoSales(live);
        } else {
          // Calculate Tito slug
          const titoSlug = generateTitoSlug(performanceRes.data);
          
          // Fetch Tito sales data
          const titoData = await fetchTitoSalesData(titoSlug);
          
          // Initialize with new data
          const initialData = {
            performanceId: id,
            salesData: {
              titoSales: titoData?.totalSold || 0,
              walkupSales: 0,
              walkupRevenue: 0,
              noShows: 0,
              guests: 0,
              totalAttendance: titoData?.totalSold || 0,
              titoRevenue: titoData?.totalRevenue || 0,
              grossRevenue: titoData?.totalRevenue || 0,
              refunds: 0,
              netRevenue: titoData?.totalRevenue || 0,
              ticketTypes: titoData?.ticketTypes || []
            }
          };
          
          // Create a new post-production report
          const createResponse = await createPostProduction({ 
            performanceId: id, 
            initialData 
          });
          
          if (createResponse.success && createResponse.data) {
            setPostProductionData(createResponse.data);
          } else {
            // Fall back to local data if creation fails
            setPostProductionData({
              ...initialPostProductionData,
              performanceId: id,
              salesData: initialData.salesData
            });
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        setLoading(false);
      }
    };
    
    if (id) fetchData();
  }, [id]);

  // Auto-calculate attendance totals
  useEffect(() => {
    if (!editMode) return;
    
    const titoSales = postProductionData.salesData.titoSales || 0;
    const walkupSales = postProductionData.salesData.walkupSales || 0;
    const guests = postProductionData.salesData.guests || 0;
    const total = titoSales + walkupSales + guests;
    
    const titoRevenue = postProductionData.salesData.titoRevenue || 0;
    const walkupRevenue = postProductionData.salesData.walkupRevenue || 0;
    const grossRevenue = titoRevenue + walkupRevenue;
    const refunds = postProductionData.salesData.refunds || 0;
    const netRevenue = grossRevenue - refunds;
    
    setPostProductionData(prev => ({
      ...prev,
      salesData: {
        ...prev.salesData,
        totalAttendance: total,
        grossRevenue: grossRevenue,
        netRevenue: netRevenue
      }
    }));
  }, [
    postProductionData.salesData.titoSales,
    postProductionData.salesData.walkupSales,
    postProductionData.salesData.guests,
    postProductionData.salesData.titoRevenue,
    postProductionData.salesData.walkupRevenue,
    postProductionData.salesData.refunds,
    editMode
  ]);

  // Tab change handler
  const handleTabChange = (_: any, newValue: number) => setTabValue(newValue);

  // Generic change handler for section fields
  const handleChange = (section: PostProductionSection, field: string, value: any) => {
    // Special handling for backline status changes
    if (section === 'technicalReport' && field === 'backlineStatus') {
      setPostProductionData(prev => {
        const currentTechReport = prev.technicalReport;
        const shouldEnableMaintenance = value === 'fair' || value === 'poor';
        const shouldDisableMaintenance = value === 'excellent' || value === 'good';
        
        return {
          ...prev,
          technicalReport: {
            ...currentTechReport,
            [field]: value,
            maintenanceRequired: shouldEnableMaintenance ? true : 
                               shouldDisableMaintenance ? false : 
                               currentTechReport.maintenanceRequired
          }
        };
      });
      return;
    }
    
    // Special case for status field
    if (section === 'status' || section === 'lastUpdated') {
      setPostProductionData(prev => ({
        ...prev,
        [section]: value
      }));
      return;
    }
    
    // Normal case for nested objects
    setPostProductionData(prev => ({
      ...prev,
      [section]: {
        ...prev[section] as Record<string, any>,
        [field]: value,
      },
    }));
  };

  // Nested change handler for arrays and nested objects
  const handleNestedChange = (
    section: PostProductionSection,
    parentField: string,
    field: string,
    value: any,
    index?: number
  ) => {
    setPostProductionData((prev) => {
      const sectionData = prev[section] as Record<string, any>;
      
      if (index !== undefined && Array.isArray(sectionData[parentField])) {
        const updatedArray = [...sectionData[parentField]];
        updatedArray[index] = { ...updatedArray[index], [field]: value };
        return {
          ...prev,
          [section]: { ...sectionData, [parentField]: updatedArray },
        };
      } else {
        return {
          ...prev,
          [section]: {
            ...sectionData,
            [parentField]: {
              ...sectionData[parentField] as Record<string, any>,
              [field]: value,
            },
          },
        };
      }
    });
  };

  // Social media handlers
  const handleSocialMediaAdd = (platform: 'instagram' | 'twitter' | 'facebook' | 'youtube' | 'other') => {
    setPostProductionData(prev => ({
      ...prev,
      mediaContent: {
        ...prev.mediaContent,
        socialMediaPosts: [
          ...prev.mediaContent.socialMediaPosts,
          { platform, url: '', engagement: 0 }
        ]
      }
    }));
  };

  const handleRemoveSocialMedia = (index: number) => {
    setPostProductionData(prev => ({
      ...prev,
      mediaContent: {
        ...prev.mediaContent,
        socialMediaPosts: prev.mediaContent.socialMediaPosts.filter((_, i) => i !== index)
      }
    }));
  };

  // Unforeseen cost handlers
  const handleUnforeseenCostAdd = () => {
    setPostProductionData(prev => ({
      ...prev,
      paymentStatus: {
        ...prev.paymentStatus,
        unforeseen_costs: [
          ...prev.paymentStatus.unforeseen_costs,
          { description: '', amount: 0 }
        ]
      }
    }));
  };

  // Status change handler
  const handleStatusChange = async (status: 'draft' | 'completed') => {
    try {
      setLoading(true);
      
      const response = await updatePostProductionStatus(
        postProductionData._id as string,
        status
      );
      
      if (response.success) {
        setPostProductionData(response.data as APIPostProductionData);
        setSuccessMessage(`Report marked as ${status}`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.error || `Failed to update report status to ${status}`);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating status:', err);
      setError(`Failed to update status to ${status}. Please try again.`);
      setLoading(false);
    }
  };

  // Save handler
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Prepare payload with type conversions
      const payload = {
        ...postProductionData,
        salesData: {
          ...postProductionData.salesData,
          titoSales: Number(postProductionData.salesData.titoSales || 0),
          walkupSales: Number(postProductionData.salesData.walkupSales || 0),
          walkupRevenue: Number(postProductionData.salesData.walkupRevenue || 0),
          noShows: Number(postProductionData.salesData.noShows || 0),
          guests: Number(postProductionData.salesData.guests || 0),
          totalAttendance: Number(postProductionData.salesData.totalAttendance || 0),
          ticketTypes: (postProductionData.salesData.ticketTypes || []).map(t => ({
            name: t.name,
            quantity: Number(t.quantity || 0),
            price: Number(t.price || 0),
          })),
        },
        paymentStatus: {
          ...postProductionData.paymentStatus,
          amountPaid: Number(postProductionData.paymentStatus.amountPaid || 0),
          unforeseen_costs: (postProductionData.paymentStatus.unforeseen_costs || []).map(c => ({
            description: c.description,
            amount: Number(c.amount || 0),
          })),
        },
      };

      const response = await updatePostProduction(
        postProductionData._id as string,
        payload
      );

      if (response.success) {
        setPostProductionData(response.data as APIPostProductionData);
        setEditMode(false);
        setSuccessMessage('Report saved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.error || 'Failed to save report');
      }
    } catch (err) {
      console.error('Error saving data:', err);
      setError('Failed to save data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          onClose={() => setSuccessMessage(null)}
          sx={{ mb: 2 }}
        >
          {successMessage}
        </Alert>
      )}

      {/* Header Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4">Post-Production Report</Typography>
            <Typography variant="h5" color="text.secondary">
              {performance?.band && typeof performance.band === 'object' 
                ? performance.band.artistName 
                : performance?.simpleBandName || 'Unknown Artist'} - {performance?.startTime 
                  ? new Date(performance.startTime).toLocaleDateString() 
                  : 'Date TBD'}
            </Typography>
            <Typography variant="subtitle1">
              Venue: {performance?.venue && typeof performance.venue === 'object' 
                ? performance.venue.name 
                : 'Unknown Venue'}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={`Status: ${postProductionData.status}`}
                color={postProductionData.status === 'completed' ? 'success' : 'warning'}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
            {editMode ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{ mr: 1 }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setEditMode(true)}
              >
                Edit Report
              </Button>
            )}
          </Grid>
        </Grid>

        {/* Quick Stats Accordion */}
        <Box sx={{ mt: 2 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Quick Stats Overview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">Total Attendance:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    {postProductionData.salesData.totalAttendance}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2">Net Revenue:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    €{postProductionData.salesData.netRevenue.toFixed(2)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2">Artist Satisfaction:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    {postProductionData.artistFeedback.artistSatisfaction}/5
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2">Audience Satisfaction:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    {postProductionData.audienceFeedback.overallRating}/5
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2">Technical Quality:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    {((postProductionData.technicalReport.soundQuality + 
                       postProductionData.technicalReport.lightingQuality) / 2).toFixed(1)}/5
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Paper>

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Sales & Attendance" />
          <Tab label="Technical Report" />
          <Tab label="Audience Feedback" />
          <Tab label="Artist Feedback" />
          <Tab label="Media Content" />
          <Tab label="Payment Status" />
          <Tab label="Summary & Assessment" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ p: 3 }}>
        {tabValue === 0 && (
          <SalesDataSection
            salesData={postProductionData.salesData}
            editMode={editMode}
            onRefreshFromTito={handleRefreshFromTito}
            onChange={(field, value) => handleChange('salesData', field, value)}
            onNestedChange={(field, subfield, value, index) => 
              handleNestedChange('salesData', field, subfield, value, index)
            }
          />
        )}

        {tabValue === 1 && (
          <TechnicalReportSection
            technicalReport={postProductionData.technicalReport}
            editMode={editMode}
            onChange={(field, value) => handleChange('technicalReport', field, value)}
          />
        )}

        {tabValue === 2 && (
          <AudienceFeedbackSection
            audienceFeedback={postProductionData.audienceFeedback}
            editMode={editMode}
            onChange={(field, value) => handleChange('audienceFeedback', field, value)}
          />
        )}

        {tabValue === 3 && (
          <ArtistFeedbackSection
            artistFeedback={postProductionData.artistFeedback}
            editMode={editMode}
            onChange={(field, value) => handleChange('artistFeedback', field, value)}
          />
        )}

        {tabValue === 4 && (
          <MediaContentSection
            mediaContent={postProductionData.mediaContent}
            editMode={editMode}
            onChange={(field, value) => handleChange('mediaContent', field, value)}
            onNestedChange={(field, subfield, value, index) => 
              handleNestedChange('mediaContent', field, subfield, value, index)
            }
            onSocialMediaAdd={handleSocialMediaAdd}
            onRemoveSocialMedia={handleRemoveSocialMedia}
          />
        )}

        {tabValue === 5 && (
          <PaymentStatusSection
            paymentStatus={postProductionData.paymentStatus}
            editMode={editMode}
            onChange={(field, value) => handleChange('paymentStatus', field, value)}
            onNestedChange={(field, subfield, value, index) => 
              handleNestedChange('paymentStatus', field, subfield, value, index)
            }
            onUnforeseenCostAdd={handleUnforeseenCostAdd}
          />
        )}

        {tabValue === 6 && (
          <SummarySection
            summary={postProductionData.summary}
            editMode={editMode}
            onChange={(field, value) => handleChange('summary', field, value)}
          />
        )}
      </Paper>

      {/* Bottom Action Bar */}
      <Paper
        sx={{
          p: 2,
          mt: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back to Performances
        </Button>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!editMode}
            sx={{ ml: 1 }}
          >
            Save Report
          </Button>
          {postProductionData.status === 'draft' && (
            <Button
              variant="contained"
              color="success"
              onClick={() => handleStatusChange('completed')}
              sx={{ ml: 1 }}
            >
              Finalize Report
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default PostProductionPage;